import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../api';

const NumberContext = createContext(null);

export function NumberProvider({ children }) {
  const [businesses, setBusinesses]       = useState([]);
  const [activeBusiness, setActiveBusiness] = useState(null);
  const [numbers, setNumbers]             = useState([]);     // numbers of active business
  const [activeNumber, setActiveNumber]   = useState(null);
  const [loaded, setLoaded]               = useState(false);

  const fetchAll = useCallback(async () => {
    const token = localStorage.getItem('wa_token');
    if (!token) { setLoaded(true); return; }
    try {
      const res  = await api.get('/businesses');
      const bizList = Array.isArray(res.data) ? res.data : [];
      setBusinesses(bizList);

      const savedBizId = parseInt(localStorage.getItem('wa_active_biz_id') || '0', 10);
      const biz = bizList.find(b => b.id === savedBizId) || bizList[0] || null;
      setActiveBusiness(biz);

      if (biz) {
        const nr = await api.get(`/businesses/${biz.id}/numbers`);
        const numList = Array.isArray(nr.data) ? nr.data : [];
        setNumbers(numList);
        const savedNumId = parseInt(localStorage.getItem('wa_active_num_id') || '0', 10);
        const num = numList.find(n => n.id === savedNumId) || numList[0] || null;
        setActiveNumber(num);
        if (num) localStorage.setItem('wa_active_num_id', String(num.id));
      } else {
        setNumbers([]); setActiveNumber(null);
      }
    } catch (e) {
      console.error('fetchAll error:', e.message);
      setBusinesses([]); setNumbers([]); setActiveNumber(null);
    } finally { setLoaded(true); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const selectBusiness = useCallback(async (biz) => {
    setActiveBusiness(biz);
    setActiveNumber(null);
    setNumbers([]);
    if (!biz) { localStorage.removeItem('wa_active_biz_id'); return; }
    localStorage.setItem('wa_active_biz_id', String(biz.id));
    try {
      const res = await api.get(`/businesses/${biz.id}/numbers`);
      const numList = Array.isArray(res.data) ? res.data : [];
      setNumbers(numList);
      const savedNumId = parseInt(localStorage.getItem('wa_active_num_id') || '0', 10);
      const num = numList.find(n => n.id === savedNumId) || numList[0] || null;
      setActiveNumber(num);
      if (num) localStorage.setItem('wa_active_num_id', String(num.id));
    } catch (e) { console.error('selectBusiness error:', e.message); }
  }, []);

  const selectNumber = useCallback((num) => {
    setActiveNumber(num);
    if (num) localStorage.setItem('wa_active_num_id', String(num.id));
    else     localStorage.removeItem('wa_active_num_id');
  }, []);

  const reset = useCallback(() => {
    setBusinesses([]); setActiveBusiness(null);
    setNumbers([]); setActiveNumber(null); setLoaded(false);
  }, []);

  // Re-fetch numbers when business changes
  const fetchNumbers = useCallback(async () => {
    if (!activeBusiness) return;
    try {
      const res = await api.get(`/businesses/${activeBusiness.id}/numbers`);
      const numList = Array.isArray(res.data) ? res.data : [];
      setNumbers(numList);
      setActiveNumber(prev => {
        if (prev && numList.find(n => n.id === prev.id))
          return numList.find(n => n.id === prev.id);
        return numList[0] || null;
      });
    } catch (e) {}
  }, [activeBusiness]);

  return (
    <NumberContext.Provider value={{
      businesses, activeBusiness, selectBusiness,
      numbers, activeNumber, selectNumber,
      fetchAll, fetchNumbers, loaded, reset,
    }}>
      {children}
    </NumberContext.Provider>
  );
}

export function useNumbers() {
  const ctx = useContext(NumberContext);
  if (!ctx) throw new Error('useNumbers must be used inside NumberProvider');
  return ctx;
}
